import reminderService from './services/reminderService';

import { format } from 'date-fns';
import i18n, { TFunction } from 'i18next';
import { Master } from './interfaces/master';
import {
  IntegrationType,
  MASTER_TYPE,
  REGEX,
  TYPE_PERIOD_MONTHLY,
  TYPE_PERIOD_QUARTERLY,
  TYPE_PERIOD_YEARLY,
} from './constants';
import { GenericObject } from './interfaces/generic-object';
import Tenant from './interfaces/tenant';
import { TiersProps } from './interfaces/tiersProps';

const breakpoints = {
  xs: 0,
  sm: 640, // Phone
  md: 768, // Tablet
  lg: 1024, // Small laptop
  xl: 1280, // Large screen / Desktop
  xxl: 1536, // Extra large screen, 4K, TV ...
};

const dataIntegrationRequiredFields = (
  integrationType: IntegrationType = IntegrationType.Reminders,
  masterType: string = MASTER_TYPE.B2B,
): { [key: string]: string[] } => {
  if (
    integrationType === IntegrationType.Cases &&
    masterType === MASTER_TYPE.B2C
  ) {
    return {
      tiers: [
        'ref',
        'addressLine1',
        'city',
        'zipCode',
        'firstName',
        'lastName',
        'phone',
        'email',
      ],
      movements: ['tiersRef', 'documentNumber', 'documentDate', 'dueDate'],
    };
  } else {
    return {
      tiers: [
        'ref',
        'legalId',
        'addressLine1',
        'city',
        'zipCode',
        'phone',
        'email',
      ],
      movements: ['tiersRef', 'documentNumber', 'documentDate', 'dueDate'],
    };
  }
};

const dataIntegrationMapping = (
  integrationType: IntegrationType = IntegrationType.Reminders,
  masterType: string = MASTER_TYPE.B2B,
): { [key: string]: string[] } => {
  if (integrationType === IntegrationType.Cases) {
    if (masterType === MASTER_TYPE.B2C) {
      return {
        tiers: [
          'ref',
          'firstName',
          'lastName',
          'phone',
          'email',
          'addressLine1',
          'addressLine2',
          'addressLine3',
          'city',
          'zipCode',
          'countryIso3',
        ],
        movements: [
          'tiersRef',
          'documentNumber',
          'type',
          'label',
          'rootAccount',
          'documentDate',
          'dueDate',
          'debit',
          'credit',
          'amount',
        ],
      };
    } else {
      return {
        tiers: [
          'ref',
          'legalId',
          'name',
          'phone',
          'email',
          'addressLine1',
          'addressLine2',
          'addressLine3',
          'city',
          'zipCode',
          'countryIso3',
        ],
        movements: [
          'tiersRef',
          'documentNumber',
          'type',
          'label',
          'rootAccount',
          'documentDate',
          'dueDate',
          'debit',
          'credit',
          'amount',
        ],
      };
    }
  }
  return {
    tiers: [
      'ref',
      'legalId',
      'name',
      'firstName',
      'lastName',
      'type',
      'phone',
      'email',
      'addressLine1',
      'addressLine2',
      'addressLine3',
      'city',
      'zipCode',
      'countryIso3',
    ],
    movements: [
      'tiersRef',
      'documentNumber',
      'type',
      'label',
      'rootAccount',
      'documentDate',
      'dueDate',
      'debit',
      'credit',
      'amount',
    ],
  };
};

const dateFormats: string[] = [
  'dd/MM/yyyy',
  'yyyy-MM-dd',
  'MM-dd-yyyy',
  'yyyyMMdd',
];

export const isNil = (value: any) => {
  return value === null || value === undefined;
};

export const isNilOrEmptyString = (value: any) => {
  return value === '' || value === null || value === undefined;
};

export const isNilOrEmpty = (value: any) => {
  if (typeof value === 'object') {
    return isNil(value) || !Object.keys(value).length;
  }
  return isNil(value) || !value.length;
};

const getBestDateFormat = (dateToTest: any) => {
  const dateRegex = REGEX.DATE;
  const dateFormat = REGEX.FORMAT;
  let foundFormat: string = 'yyyyMMdd';
  Object.values(dateRegex).forEach((value: any, index: number) => {
    if (value.test(dateToTest)) {
      foundFormat = dateFormat[Object.keys(dateRegex)[index]];
    }
  });
  return foundFormat;
};

const getToday = () => {
  return format(new Date(), 'yyyy-MM-dd').toString();
};

const getTomorrow = () => {
  const tomorrow = new Date();
  tomorrow.setDate(new Date().getDate() + 1);
  return tomorrow;
};

const getMailToUrl = async ({
  fileUrl,
  recipient,
  emailClient,
}: {
  fileUrl: string;
  recipient: string;
  emailClient: string;
}): Promise<string> => {
  const data = await reminderService.getFile({ url: fileUrl });
  const res = await data;
  const mailContent: string = new TextDecoder().decode(res.data);

  let urlStart: string = 'mailto:';
  let urlSubjectPart: string = '?subject=';
  const urlBodyPart: string = '&body=';

  if (emailClient === 'gmail') {
    urlStart = 'https://mail.google.com/mail/?view=cm&fs=1&to=';
    urlSubjectPart = '&su=';
  }

  const link: string = `${urlStart}${recipient}${urlSubjectPart}${getSubjectEmailReminder(
    mailContent,
  )}${urlBodyPart}${getBodyEmailReminder(mailContent)}`;
  return link.replaceAll('\n', '%0D%0A');
};

export const getLanguage = () => {
  return i18n.language?.includes('fr') ? 'FR' : 'EN';
};

const getLocale = () => {
  return i18n.language?.includes('fr') ? 'fr-FR' : 'en-US';
};
const combineTwoArrays = (
  baseArray: any[],
  arrayToCombine: any[] | undefined,
  key: string,
  ids: any,
) => {
  const final = [...baseArray];
  if (!arrayToCombine) return final;
  arrayToCombine.forEach((element: any, index: number) => {
    const ref = arrayToCombine[index][key];
    const foundKey = baseArray.find((o: any) => o[key] === ref);
    if (!foundKey) {
      final.push(element);
    }
  });

  return final.filter((el: any) => {
    return ids[el[key]];
  });
};
const capitalize = (value?: string) => {
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
};

const getSubjectEmailReminder = (mailContent: string): string => {
  let subject = mailContent.split('SUBJECT:')[1];
  subject = subject?.split('BODY:')[0];
  return subject?.trim();
};

const getSignUrl = (signMethod: string, docusignDocId: any, fileId: any) => {
  return signMethod === 'PUT'
    ? `?envelope-id=${docusignDocId}`
    : (fileId as string);
};

const getBodyEmailReminder = (mailContent: string): string => {
  const body = mailContent?.split('BODY:')[1];
  return body?.trim();
};
const objToString = (obj: any): string => {
  let str = '';
  for (const p in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, p)) {
      str += `${p}:${obj[p]}`;
    }
  }
  return str;
};

const multiObjToString = (arr: any[] | undefined): string => {
  if (!arr) return '';
  const str: string[] = [];
  arr.forEach((obj) => {
    str.push(`ids=${obj.type}:${obj.value}`);
  });
  return str.join('&');
};

const addMasterRefProps = (master: Master | undefined) => {
  if (!master) return null;
  return {
    masterRef: master.reference,
    masterCurrency: master.settings.accountingCurrency,
    masterAddress: master.address,
  };
};

const dataIntegrationErrorFormatter = (errors: any[]) => {
  if (!errors) return [];
  const results: any[] = [];
  errors.forEach((row: any) => {
    row.errors.forEach((col: any) => {
      results.push({ row: row.rowNumber, col: col.colNumber });
    });
  });
  return results;
};

const dataIntegrationReformatErrors = (errorsData: any) => {
  interface Error {
    colNumber: number;
    error: string;
    error_description: string;
    code: string;
  }

  interface RowError {
    rowNumber: number;
    errors: Error[];
  }

  if (!errorsData) return [];
  return errorsData.reduce((acc: any, row: RowError) => {
    row.errors.forEach((error) => {
      const colNumber: number = error.colNumber;
      acc[colNumber] = acc[colNumber] || [];
      acc[colNumber].message =
        acc[colNumber].message || error.error_description;
      acc[colNumber].code = acc[colNumber].code || error.error;
      acc[colNumber].colNumber = acc[colNumber].colNumber || colNumber;
      acc[colNumber].lines = acc[colNumber].lines || [];
      acc[colNumber].lines.push({ line: row.rowNumber });
    });
    return acc;
  }, {});
};

const transformReminder = (reminder: any) => {
  return {
    comment: reminder.comment,
    createdDate: reminder.date,
    endComment: reminder.detail.endComment,
    endDate: reminder.detail.endDate,
    id: reminder.detail.id,
    url: reminder.detail.url,
    title: reminder.title,
  };
};

export const getFileUrlFromArrayBuffer = (
  bytes: ArrayBuffer,
  blobType: string = 'application/pdf',
): string => {
  const bytesArray = new Uint8Array(bytes);
  const blob = new Blob([bytesArray], { type: blobType });
  return URL.createObjectURL(blob);
};

const getFileLoad = (bytes: ArrayBuffer, fileName: string): any => {
  const bytesArray = new Uint8Array(bytes);
  const blob = new Blob([bytesArray], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link?.parentNode?.removeChild(link);
};

const getEmailFromTiers = (tiers: TiersProps | undefined): string => {
  if (tiers?.collectionContact && tiers?.collectionContact?.email) {
    return tiers.collectionContact?.email;
  }
  if (tiers?.email) {
    return tiers.email;
  }

  return '';
};

const getKeyByValue = (object: any, value: string) => {
  const values = Object.values(object).filter((item) => item === value);
  if (values.length !== 1) return void 0;

  return Object.keys(object).find((key) => object[key] === value);
};

const getArraysIntersection = ({
  arr1,
  arr2,
}: {
  arr1: string[];
  arr2: string[];
}): string[] => {
  const setFromArray = new Set(arr2);
  return Array.from(new Set(arr1)).filter((item) => setFromArray.has(item));
};

const getArraysDifference = ({
  arr1,
  arr2,
}: {
  arr1: string[];
  arr2: string[];
}): string[] => {
  const setFromArray = new Set(arr2);
  return Array.from(new Set(arr1)).filter((x) => !setFromArray.has(x));
};

const arraysHasDuplicateEntries = (array: string[]): boolean => {
  const setFromArray = new Set(array);
  return setFromArray.size !== array.length;
};

const getDuplicatesFromArray = (array: string[]): string[] => {
  const dupicates: string[] = array.filter(
    (item: string, index: number) => array.indexOf(item) !== index,
  );

  return Array.from(new Set(dupicates));
};

const getDuplicatesHeader = (array: string[]): string[] => {
  const counts: any = {};
  return array.map((u: string) => {
    if (counts[u]) {
      counts[u] += 1;
      u += ` (${counts[u]})`;
    } else if (array.filter((a: string) => a === u).length > 1) {
      counts[u] = 1;
      u += ` (${counts[u]})`;
    }
    return u;
  });
};

const removeKeysWithEmptyValueFromObject = (
  obj: {
    [key: string]: any;
  },
  uploadType: any,
  requiredFields?: GenericObject<string[]>,
): { [key: string]: any } => {
  if (requiredFields) {
    return Object.keys(obj)
      .filter(
        (k) =>
          obj[k] !== '' && requiredFields[uploadType]?.indexOf(obj[k]) !== -1,
      )
      .reduce((a, k) => ({ ...a, [k]: obj[k] }), {});
  } else {
    return Object.keys(obj)
      .filter((k) => obj[k] !== '')
      .reduce((a, k) => ({ ...a, [k]: obj[k] }), {});
  }
};

export const clearEmptyKeys = <
  T extends Record<string, any> = { [key: string]: any },
>(
  obj: T,
  omitted?: string[],
): T extends { [key: string]: any } ? { [key: string]: any } : Partial<T> => {
  const cleanedObj = {} as any;
  for (const key in obj) {
    if ((omitted && omitted.includes(key)) || !isNilOrEmptyString(obj[key])) {
      cleanedObj[key] = obj[key];
    }
  }
  return cleanedObj;
};

const objectsHaveSameKeys = (...objects: any[]): boolean => {
  const allKeys = objects.reduce(
    (keys, object) => keys.concat(Object.keys(object)),
    [],
  );
  const union = new Set(allKeys);
  return objects.every((object) => union.size === Object.keys(object).length);
};

const convertBytesToKB = (
  bytes: number,
  unit: boolean = true,
): number | string => {
  const round: number = Math.round(bytes / 1024);
  const unitLang = getLanguage() === 'FR' ? 'Ko' : 'Kb';
  return unit ? `${round} ${unitLang}` : round;
};

const windowScroll = () => {
  return window.scrollTo({
    top: document.body.scrollHeight,
    left: 0,
    behavior: 'smooth',
  });
};

const getBaseUrl = (url: URL) => {
  let baseUrl = `${url.protocol}//${url.hostname}`;
  if (url.port) {
    baseUrl += `:${url.port}`;
  }
  return baseUrl;
};

const convertBytesToMb = (bytes: number) => {
  const round = bytes / Math.pow(1024, 2);
  return parseFloat(round.toString()).toFixed(round > 1 ? 0 : 1);
};

const checkValuePositive = (someArray: any) => {
  return someArray.reduce(
    (accumulator: any, currentValue: any) => accumulator + currentValue,
    0,
  );
};

const valueOrEmptyString = (value: any) => value || '';

const areArraysEqual = (array1: any[], array2: any[]): boolean => {
  if (array1.length !== array2.length) {
    return false;
  }

  for (let i = 0; i < array1.length; i++) {
    if (JSON.stringify(array1[i]) !== JSON.stringify(array2[i])) {
      return false;
    }
  }

  return true;
};

export const getInitialsFromName = (name: string) => {
  if (!name) return '';
  const formattedName = name?.replaceAll('-', ' ').replaceAll('_', ' ');
  const splittedName = formattedName.split(' ');
  let initials = '';
  splittedName.forEach((n) => {
    if (initials.length < 2) {
      initials += n.slice(0, 1);
    }
  });
  return initials.toUpperCase();
};

const numberFormatter = (value: number) => {
  return new Intl.NumberFormat(getLocale(), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const getClassRed = (value: boolean) => {
  return value ? 'red' : '';
};

const scrollWithOffset = (el: HTMLElement, yOffset: number) => {
  const yCoordinate = el.getBoundingClientRect().top + window.scrollY;
  window.scrollBy({ top: yCoordinate + yOffset, behavior: 'smooth' });
};

const replaceNbsps = (str?: string) => {
  if (!str) return '';
  const re = new RegExp(String.fromCharCode(160), 'g');
  return str.replace(re, ' ');
};

export const getFirstPath = (
  pathname: string,
  slash: boolean = true,
): string => {
  const paths = pathname?.split('/').filter(Boolean);
  const _slash = slash ? '/' : '';
  return paths?.length > 0 ? `${_slash}${paths[0]}` : '';
};

export const getIntegrationType = (pathname: string) => {
  return getFirstPath(pathname) === '/import-cases'
    ? IntegrationType.Cases
    : IntegrationType.Reminders;
};

export const extractUUIDFromGedFilePath = (url: string): string => {
  const parts = url.split('/');
  return parts[parts.length - 1];
};

export const getTiersName = (tiers) => {
  if (
    (tiers?.type === 'B2B' && tiers?.name) ||
    (tiers?.type === 'B2C' && !tiers?.physicalIdentity)
  ) {
    return tiers?.name;
  }
  if (tiers?.type === 'B2C' && tiers?.physicalIdentity) {
    return tiers?.physicalIdentity?.title === 'UNK'
      ? `${utils.capitalize(
          tiers?.physicalIdentity?.firstName,
        )} ${tiers?.physicalIdentity?.lastName?.toUpperCase()}`
      : `${i18n.t(
          `civility.${tiers?.physicalIdentity?.title}`,
        )} ${utils.capitalize(
          tiers?.physicalIdentity?.firstName,
        )} ${tiers?.physicalIdentity?.lastName?.toUpperCase()}`;
  }
  return '';
};

export const getCurrentQuarter = (): string => {
  const date = new Date();
  if (date.getMonth() >= 0 && date.getMonth() <= 2) {
    // Q1
    return '1';
  } else if (date.getMonth() <= 3 && date.getMonth() <= 5) {
    // Q2
    return '2';
  } else if (date.getMonth() <= 6 && date.getMonth() <= 9) {
    // Q3
    return '3';
  } else {
    // Q4
    return '4';
  }
};

export const getYearsSinceTenantCreation = (tenant?: Tenant) => {
  const years: { label: string; value: string }[] = [];

  if (tenant?.createdDate) {
    const firstYearToDisplay = new Date(
      tenant.createdDate.replace(/\.\d+$/, ''),
    ).getFullYear();
    // Si 0, ça veut dire une entrée.
    const nbEntrees = new Date().getFullYear() - firstYearToDisplay;
    for (let i = 0; i <= nbEntrees; i++) {
      const toInsert = (new Date().getFullYear() - i).toString();
      years.push({
        label: toInsert,
        value: toInsert,
      });
    }
  }

  return years.length === 0
    ? years.concat({
        label: new Date().getFullYear().toString(),
        value: new Date().getFullYear().toString(),
      })
    : years;
};

export const getMonthlyDividedYear = (monthLabels: string[]) => [
  {
    label: monthLabels[0],
    value: '1',
  },
  {
    label: monthLabels[1],
    value: '2',
  },
  {
    label: monthLabels[2],
    value: '3',
  },
  {
    label: monthLabels[3],
    value: '4',
  },
  {
    label: monthLabels[4],
    value: '5',
  },
  {
    label: monthLabels[5],
    value: '6',
  },
  {
    label: monthLabels[6],
    value: '7',
  },
  {
    label: monthLabels[7],
    value: '8',
  },
  {
    label: monthLabels[8],
    value: '9',
  },
  {
    label: monthLabels[9],
    value: '10',
  },
  {
    label: monthLabels[10],
    value: '11',
  },
  {
    label: monthLabels[11],
    value: '12',
  },
];

export const getMonthlyDividedQuarter = (quarterLabels: string[]) => {
  return [
    {
      label: quarterLabels[0],
      value: '1',
    },
    {
      label: quarterLabels[1],
      value: '2',
    },
    {
      label: quarterLabels[2],
      value: '3',
    },
    {
      label: quarterLabels[3],
      value: '4',
    },
  ];
};

export const getAllPeriodTypesFilterForPeriodicalStatistics = (
  periodTypes: string[],
) => [
  {
    label: periodTypes[0],
    value: TYPE_PERIOD_YEARLY,
  },
  {
    label: periodTypes[1],
    value: TYPE_PERIOD_QUARTERLY,
  },
  {
    label: periodTypes[2],
    value: TYPE_PERIOD_MONTHLY,
  },
];

const getWeeklyDividedMonth = (languageIsFrench: boolean) =>
  languageIsFrench
    ? ['S1', 'S2', 'S3', 'S4', 'S5']
    : ['W1', 'W2', 'W3', 'W4', 'W5'];

const getLabelsForYearlyTimePeriod = (
  t: TFunction<'translation', undefined>,
) => {
  return [
    t('dashboard-stats.periodical.legend-label.jan'),
    t('dashboard-stats.periodical.legend-label.fev'),
    t('dashboard-stats.periodical.legend-label.mar'),
    t('dashboard-stats.periodical.legend-label.avr'),
    t('dashboard-stats.periodical.legend-label.mai'),
    t('dashboard-stats.periodical.legend-label.jun'),
    t('dashboard-stats.periodical.legend-label.jul'),
    t('dashboard-stats.periodical.legend-label.aou'),
    t('dashboard-stats.periodical.legend-label.sep'),
    t('dashboard-stats.periodical.legend-label.oct'),
    t('dashboard-stats.periodical.legend-label.nov'),
    t('dashboard-stats.periodical.legend-label.dec'),
  ];
};

const getLabelsForQuarterlyTimePeriod = (
  quarterNumber: number,
  t: TFunction<'translation', undefined>,
) => {
  switch (quarterNumber) {
    case 1:
      return [
        t('dashboard-stats.periodical.legend-label.jan'),
        t('dashboard-stats.periodical.legend-label.fev'),
        t('dashboard-stats.periodical.legend-label.mar'),
      ];
    case 2:
      return [
        t('dashboard-stats.periodical.legend-label.avr'),
        t('dashboard-stats.periodical.legend-label.mai'),
        t('dashboard-stats.periodical.legend-label.jun'),
      ];
    case 3:
      return [
        t('dashboard-stats.periodical.legend-label.jul'),
        t('dashboard-stats.periodical.legend-label.aou'),
        t('dashboard-stats.periodical.legend-label.sep'),
      ];
    case 4:
      return [
        t('dashboard-stats.periodical.legend-label.oct'),
        t('dashboard-stats.periodical.legend-label.nov'),
        t('dashboard-stats.periodical.legend-label.dec'),
      ];
  }
};

export const withoutFutureQuarters = (
  quarters: { label: string; value: string }[],
) => {
  const currentQuarter = Math.ceil((new Date().getMonth() + 1) / 3) as
    | 1
    | 2
    | 3
    | 4;

  return quarters.filter((quarter) => Number(quarter.value) <= currentQuarter);
};

export const withoutFutureMonths = (
  months: { label: string; value: string }[],
) => {
  const currentMonth = new Date().getMonth() + 1; // 1 à 12

  return months.filter((month) => Number(month.value) <= currentMonth);
};

/**
 * Formate un SIREN (9 chiffres) avec des espaces.
 * @param siren - Le numéro SIREN (string ou number)
 * @returns Le SIREN formaté (ex: "123 456 789") ou la valeur brute si invalide.
 */
export const formatSiren = (
  siren: string | number | undefined | null,
): string => {
  if (!siren) return '';
  // On ne garde que les chiffres au cas où il y aurait déjà des espaces ou des tirets
  const cleaned = siren.toString().replace(/\D/g, '');

  // SIREN
  if (cleaned.length === 9) {
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3');
  }

  // SIRET
  if (cleaned.length === 14) {
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{5})/, '$1 $2 $3 $4');
  }

  return cleaned;
};

export const daysSinceDate = (date: string) =>
  Math.floor((Date.now() - parseInt(date, 10)) / (1000 * 60 * 60 * 24));

export async function generateSHA256(message: string): Promise<string> {
  const msgBuffer = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

const utils = {
  breakpoints,
  capitalize,
  dataIntegrationRequiredFields,
  dataIntegrationMapping,
  dateFormats,
  dataIntegrationErrorFormatter,
  dataIntegrationReformatErrors,
  transformReminder,
  getMailToUrl,
  isNil,
  isNilOrEmptyString,
  isNilOrEmpty,
  getBestDateFormat,
  objToString,
  multiObjToString,
  getLanguage,
  getLocale,
  getSubjectEmailReminder,
  getBodyEmailReminder,
  addMasterRefProps,
  getFileUrlFromArrayBuffer,
  getFileLoad,
  getKeyByValue,
  getEmailFromTiers,
  getSignUrl,
  getArraysIntersection,
  getArraysDifference,
  getToday,
  getTomorrow,
  arraysHasDuplicateEntries,
  getDuplicatesFromArray,
  removeKeysWithEmptyValueFromObject,
  objectsHaveSameKeys,
  getDuplicatesHeader,
  convertBytesToKB,
  windowScroll,
  getBaseUrl,
  convertBytesToMb,
  valueOrEmptyString,
  combineTwoArrays,
  checkValuePositive,
  areArraysEqual,
  getInitialsFromName,
  numberFormatter,
  getClassRed,
  scrollWithOffset,
  replaceNbsps,
  getFirstPath,
  extractUUIDFromGedFilePath,
  getCurrentQuarter,
  getYearsSinceTenantCreation,
  getMonthlyDividedYear,
  getMonthlyDividedQuarter,
  getLabelsForMonthlyTimePeriod: getWeeklyDividedMonth,
  getAllPeriodTypesFilterForPeriodicalStatistics,
  getLabelsForQuarterlyTimePeriod,
  getLabelsForYearlyTimePeriod,
  withoutFutureQuarters,
  withoutFutureMonths,
};

export default utils;
